import axios from 'axios';
import { API_BASE_URL } from '../constants/api';

const API_BASE = API_BASE_URL;

export const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true, // send httpOnly auth cookie on every request
  timeout: 30000, // 30 second network timeout
});

// ── CSRF token management ────────────────────────────────────────────────────
// The backend exposes GET /api/csrf-token which returns an HMAC-signed token.
// We fetch it once on app init and attach it to all non-GET requests. On the
// first state-changing request after a failed init we lazy-retry once.
let _csrfToken = null;
let _csrfInitFailed = false;
let _csrfRetryInFlight = null;

const fetchCsrfToken = async () => {
  const res = await api.get('/csrf-token');
  _csrfToken = res.data.csrfToken;
  _csrfInitFailed = false;
  return _csrfToken;
};

export const initializeCsrf = async () => {
  try {
    await fetchCsrfToken();
  } catch {
    _csrfInitFailed = true;
  }
};

export const getCsrfToken = () => _csrfToken;
export const hasCsrfInitFailed = () => _csrfInitFailed;

// Request interceptor — attach CSRF token to all non-GET requests. If we
// failed to fetch a token during init AND this is a state-changing request,
// try once more before sending. Avoids the silent-403 trap where the user
// clicks Save and nothing happens.
api.interceptors.request.use(async config => {
  if (config.method && config.method !== 'get') {
    if (!_csrfToken && _csrfInitFailed) {
      _csrfRetryInFlight = _csrfRetryInFlight || fetchCsrfToken().catch(() => null).finally(() => { _csrfRetryInFlight = null; });
      try { await _csrfRetryInFlight; } catch { /* ignore */ }
    }
    if (_csrfToken) config.headers['X-CSRF-Token'] = _csrfToken;
  }
  return config;
});

// Response interceptor — pass errors through; AuthContext manages session via /auth/me
api.interceptors.response.use(
  response => response,
  error => Promise.reject(error)
);

// Events
export const getEvents = (params) => api.get('/events', { params });
export const getFeaturedEvents = () => api.get('/events/featured');
export const searchCities = (q = '', limit = 10) => api.get('/events/cities', { params: { q, limit } });
export const getEvent = (id) => api.get(`/events/${id}`);
export const createEvent = (data) => api.post('/events', data);
export const updateEvent = (id, data) => api.put(`/events/${id}`, data);
export const getMyAnalytics = () => api.get('/events/my-analytics');
export const trackTicketClick = (id) => api.post(`/events/${id}/ticket-click`);
export const purchaseTicket = (data) => api.post('/tickets/purchase', data);
export const confirmTicketPayment = (ticketId) => api.post(`/tickets/${ticketId}/confirm`);
export const getMyTickets = () => api.get('/tickets/my-tickets');
export const trackShare = (id) => api.post(`/events/${id}/share`);
export const rsvpEvent = (id) => api.post(`/events/${id}/rsvp`);
export const saveEvent = (id) => api.post(`/events/${id}/save`);
export const getSavedEvents = () => api.get('/users/saved-events');

// Articles
export const getArticles = (params) => api.get('/articles', { params });
export const getArticlesFeed = (params) => api.get('/articles/feed', { params }); // Fresh feed (chronological, for Magazine redesign)
export const getArticlesTrending = () => api.get('/articles/trending'); // Trending articles (engagement-based)
export const getFeaturedArticles = () => api.get('/articles/featured');
export const getArticle = (slug) => api.get(`/articles/${slug}`);
export const getSpotlightArticle = () => api.get('/articles/spotlight');
export const getAfricanBusinessArticles = (limit = 3) => api.get(`/articles/african-business?limit=${limit}`);
export const getWhosBuilding = (limit = 3) => api.get(`/articles/whos-building?limit=${limit}`);
// The magazine as a publication. `current` answers null — not 404 — when no
// issue has been published, because an empty magazine is a state, not an error.
export const getCurrentIssue = () => api.get('/issues/current');
export const getIssues = () => api.get('/issues');
export const getIssue = (slug) => api.get(`/issues/${slug}`);
export const likeArticle = (slug) => api.post(`/articles/${slug}/like`);
export const getWatchHistory = () => api.get('/watch-history');
export const saveWatchProgress = (slug, positionSec, completed = false) =>
  api.post(`/articles/${slug}/progress`, { positionSec, completed });
export const getArticleLikes = (slug) => api.get(`/articles/${slug}/likes`);
export const reactToArticle = (slug, emoji) => api.post(`/articles/${slug}/react`, { emoji });
export const getArticleReactions = (slug) => api.get(`/articles/${slug}/reactions`);

// Partners
export const applyForPartnership = (data) => api.post('/partners/apply', data);
export const getPartnerApplications = (params) => api.get('/partners', { params });
export const getPartnerApplication = (id) => api.get(`/partners/${id}`);
export const approvePartnerApplication = (id) => api.put(`/partners/${id}/approve`);
export const rejectPartnerApplication = (id, reason) => api.put(`/partners/${id}/reject`, { reason });
export const deactivatePartnerApplication = (id) => api.put(`/partners/${id}/deactivate`);
export const deletePartnerApplication = (id) => api.delete(`/partners/${id}`);

// Ads

// Homepage settings (public endpoint for sponsor banner + featured events)
export const getHomepageSettings = () => api.get('/admin/homepage-settings');

// Sponsors
export const getCurrentSponsor = () => api.get('/sponsors/current');

// Buyer-facing service fee, so the ticket box can show a total that matches
// what Stripe will charge. Falls back to the platform defaults if unreachable.
export const getFeeConfig = () => api.get('/config/fees');
export const getTrendingArticles = () => api.get('/sponsors/trending');
export const getSponsorSlots = () => api.get('/sponsors/slots');
export const createSponsorSlot = (data) => api.post('/sponsors/slots', data);
export const updateSponsorSlot = (id, data) => api.put(`/sponsors/slots/${id}`, data);
export const deleteSponsorSlot = (id) => api.delete(`/sponsors/slots/${id}`);
export const getSponsorRevenue = () => api.get('/sponsors/revenue');
export const getSponsorReport = () => api.get('/sponsors/report');

// Newsletter
export const subscribeNewsletter = (data) => api.post('/newsletter/subscribe', data);
export const unsubscribeNewsletter = (data) => api.post('/newsletter/unsubscribe', data);
export const getNewsletterSubscribers = (params) => api.get('/newsletter/subscribers', { params });

// Users
export const getUserProfile = () => api.get('/users/profile');
export const updateUserProfile = (data) => api.put('/users/profile', data);
export const getUserEvents = () => api.get('/users/my-events');

// Upload image to Cloudinary via backend
// Accepts a File object, returns the Cloudinary URL string
export const uploadImage = async (file, folder = 'events') => {
  const form = new FormData();
  form.append('image', file);
  form.append('folder', folder);
  const res = await api.post('/upload/image-stream', form, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 60000,
  });
  return res.data.url;
};

// Compress image on client before uploading (reduces upload size ~70%)
export const compressImage = (file, maxWidth = 1200, quality = 0.82) => new Promise((resolve) => {
  const reader = new FileReader();
  reader.onload = (e) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      let { width, height } = img;
      if (width > maxWidth) {
        height = Math.round(height * maxWidth / width);
        width = maxWidth;
      }
      canvas.width = width;
      canvas.height = height;
      canvas.getContext('2d').drawImage(img, 0, 0, width, height);
      canvas.toBlob(
        blob => {
          if (!blob) { resolve(file); return; }
          // Return as a proper File so FileReader gets the correct mime type
          const compressed = new File([blob], file.name || 'image.jpg', { type: 'image/jpeg' });
          resolve(compressed);
        },
        'image/jpeg',
        quality
      );
    };
    img.onerror = () => resolve(file);
    img.src = e.target.result;
  };
  reader.onerror = () => resolve(file);
  reader.readAsDataURL(file);
});

export default api;
